// Iter 158q — Page publique de saisie autonome des pièces d'identité (lien personnel par jeton)
import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import RemplacerParticipant from '../components/espace/RemplacerParticipant';
import { LiterieChoix } from '../components/espace/LiterieChoix';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const NATIONALITES = ['Française', 'Algérienne', 'Marocaine', 'Tunisienne'];

const ALL_FIELDS = [
  { key: 'titre', label: 'Titre', type: 'select', options: ['M.', 'Mme'] },
  { key: 'genre', label: 'Genre (sur la pièce)', type: 'select', options: ['M', 'F'] },
  { key: 'nationalite', label: 'Nationalité', type: 'nationalite' },
  { key: 'dateNaissance', label: 'Date de naissance', type: 'text', placeholder: 'JJ/MM/AAAA' },
  { key: 'lieuNaissance', label: 'Lieu de naissance', type: 'text', placeholder: 'Ex : Lyon' },
  { key: 'typeIdentite', label: 'Type de pièce', type: 'select', options: ['Passeport', "Carte d'identité"] },
  { key: 'numeroIdentite', label: 'Numéro de la pièce', type: 'text', placeholder: 'Ex : 19FR45678' },
  { key: 'dateDelivrance', label: 'Délivrée le', type: 'text', placeholder: 'JJ/MM/AAAA' },
  { key: 'lieuDelivrance', label: 'Lieu de délivrance', type: 'text', placeholder: 'Ex : France' },
  { key: 'dateExpiration', label: 'Expire le', type: 'text', placeholder: 'JJ/MM/AAAA' },
];

export default function MesPieces() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [form, setForm] = useState({});
  const [error, setError] = useState('');
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  const [certifie, setCertifie] = useState(false);
  const [doneWarnings, setDoneWarnings] = useState([]);
  const [doneInfo, setDoneInfo] = useState(null); // Iter 605 — message / modifications en attente de validation
  const [autreNat, setAutreNat] = useState({});

  // Iter 832 — champs supplémentaires (langue, urgence…) et rubriques libres définis côté serveur
  const fieldDefs = (d) => {
    const extra = (d?.field_defs || []).filter(f => !ALL_FIELDS.some(a => a.key === f.key)).map(f => ({ key: f.key, label: f.label, type: 'text', placeholder: f.placeholder || '' }));
    return [...ALL_FIELDS, ...extra];
  };
  const load = () => axios.get(`${API}/pieces-identite/${token}`)
      .then(r => {
        setData(r.data);
        const keys = r.data.fields?.length ? r.data.fields : ALL_FIELDS.map(f => f.key);
        const init = {};
        r.data.voyageurs.forEach(v => {
          init[v.index] = {};
          keys.forEach(k => { init[v.index][k] = v[k] || ''; });
          if (keys.includes('nationalite') && !init[v.index].nationalite) init[v.index].nationalite = 'Française';
        });
        setForm(init);
      })
      .catch(() => setError("Ce lien n'est pas valide ou a expiré. Contactez-nous au 06 60 41 62 18."));
  useEffect(() => { load(); }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  const setVal = (idx, key, val) => setForm(p => ({ ...p, [idx]: { ...p[idx], [key]: val } }));

  // Iter 176 (Kamel) — slash automatique pour les dates : on tape 05031990 → 05/03/1990
  const formatDateInput = (val) => {
    const d = val.replace(/\D/g, '').slice(0, 8);
    if (d.length <= 2) return d;
    if (d.length <= 4) return `${d.slice(0, 2)}/${d.slice(2)}`;
    return `${d.slice(0, 2)}/${d.slice(2, 4)}/${d.slice(4)}`;
  };
  const passeportOnly = !!data?.passeport_uniquement;
  const isAvion = !!data?.isAvion;

  // Iter 177 (Kamel) — contrôle expiration : avion = 6 mois après le retour ;
  // car (Iter 208) = valide jusqu'au retour du voyage seulement. Non bloquant.
  // Iter 307 — exemption : passeport algérien pour l'Algérie = valide jusqu'au retour, pas de règle 6 mois.
  const expWarning = (val, nat) => {
    const exempt = !!data?.nationalite_exemptee_6mois
      && (nat || '').trim().toLowerCase() === data.nationalite_exemptee_6mois.toLowerCase();
    const min = ((exempt ? data?.expiration_fin_voyage : data?.expiration_minimale) || '').slice(0, 10);
    if (!min || !val || val.length !== 10) return null;
    const [d, m, y] = val.split('/');
    if (!d || !m || !y || y.length !== 4) return null;
    const iso = `${y}-${m}-${d}`;
    if (Number.isNaN(new Date(`${iso}T00:00:00`).getTime())) return null;
    if (iso >= min) return null;
    const minDisp = min.split('-').reverse().join('/');
    if (exempt) return `⚠️ Ce passeport expire le ${val} : il doit rester valide jusqu'au retour du voyage (le ${minDisp}). Faites-le renouveler avant le départ, puis revenez saisir le nouveau — ce lien reste valable.`;
    return isAvion
      ? `⚠️ Ce passeport expire le ${val} : il doit rester valide au moins 6 MOIS après le retour du voyage (soit jusqu'au ${minDisp} minimum). Faites-le renouveler avant le départ, puis revenez saisir le nouveau — ce lien reste valable.`
      : `⚠️ Cette pièce expire le ${val} : elle doit rester valide jusqu'au retour du voyage (le ${minDisp}). Faites-la renouveler avant le départ, puis revenez saisir la nouvelle — ce lien reste valable.`;
  };

  const submit = async () => {
    if (!certifie) {
      alert("Merci de cocher la case certifiant que les informations sont recopiées exactement depuis votre pièce d'identité.");
      return;
    }
    setSending(true);
    try {
      const voyageurs = Object.entries(form).map(([index, vals]) => ({ index: parseInt(index, 10), ...vals }));
      const r = await axios.post(`${API}/pieces-identite/${token}`, { voyageurs, certifie: true });
      setDoneWarnings(r.data.warnings || []);
      setDoneInfo({ message: r.data.message || '', enAttente: r.data.en_attente || [], updated: r.data.updated || [] });
      setDone(true);
    } catch (e) {
      alert('⚠️ ' + (e.response?.data?.detail || e.message));
    }
    setSending(false);
  };

  if (error) return (
    <div className="min-h-screen bg-[#FAF7F2] flex items-center justify-center p-6">
      <div className="bg-white rounded-2xl shadow-lg p-8 max-w-md text-center" data-testid="pieces-error">
        <div className="text-4xl mb-3">😕</div>
        <p className="text-[#1E3A5F] font-semibold">{error}</p>
      </div>
    </div>
  );

  if (!data) return (
    <div className="min-h-screen bg-[#FAF7F2] flex items-center justify-center">
      <p className="text-[#1E3A5F]">Chargement…</p>
    </div>
  );

  if (done) return (
    <div className="min-h-screen bg-[#FAF7F2] flex items-center justify-center p-6">
      <div className="bg-white rounded-2xl shadow-lg p-8 max-w-md text-center" data-testid="pieces-done">
        <div className="text-5xl mb-3">🎉</div>
        <h1 className="text-2xl font-bold text-[#1E3A5F] mb-2">Merci !</h1>
        {doneInfo?.enAttente?.length > 0 && !doneInfo.updated.length ? (
          <p className="text-gray-700" data-testid="pieces-done-en-attente">⏳ {doneInfo.message}</p>
        ) : doneInfo?.message && !doneInfo.updated.length && !doneInfo.enAttente.length ? (
          <p className="text-gray-700" data-testid="pieces-done-identique">✅ {doneInfo.message} Rien à faire de votre côté pour le voyage <strong>{data.voyage}</strong>.</p>
        ) : (
          <>
            <p className="text-gray-600">Vos informations ont bien été enregistrées pour le voyage <strong>{data.voyage}</strong>. Votre dossier est à jour — à très bientôt !</p>
            {doneInfo?.enAttente?.length > 0 && <p className="mt-2 text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-2" data-testid="pieces-done-en-attente">⏳ {doneInfo.message}</p>}
          </>
        )}
        {doneWarnings.length > 0 && (
          <div className="mt-4 bg-orange-50 border border-orange-200 rounded-lg p-3 text-left">
            {doneWarnings.map((w, i) => <p key={i} className="text-sm text-orange-700">⚠️ {w}</p>)}
          </div>
        )}
        <p className="text-sm text-gray-400 mt-4">Rhône Alpes Amitié Voyages</p>
      </div>
    </div>
  );

  // Iter 164 (Kamel) — TOUT OU RIEN : la compagnie exige un dossier complet.
  const requiredKeys = data.fields?.length ? data.fields : ALL_FIELDS.map(f => f.key);
  const labelOf = (k) => (fieldDefs(data).find(f => f.key === k)?.label || k);
  const missingByVoyageur = data.voyageurs.map(v => ({
    nom: `${v.prenom} ${v.nom}`,
    manque: requiredKeys.filter(k => !((form[v.index]?.[k] || '').trim())).map(labelOf),
  })).filter(x => x.manque.length > 0);
  const isComplete = missingByVoyageur.length === 0;
  const missCls = (idx, key) => ((form[idx]?.[key] || '').trim() ? '' : ' border-red-300 bg-red-50');

  // Iter 808 (Kamel) — journée / voyage sans formalités : pas de pièce d'identité, seulement le(s) remplacement(s) autorisé(s)
  if (data.pieces_requises === false) {
    return (
      <div className="min-h-screen bg-[#FAF7F2] py-8 px-4">
        <div className="max-w-2xl mx-auto">
          <div className="bg-[#1E3A5F] text-white rounded-t-2xl p-6">
            <p className="text-sm text-[#79c9ea] font-semibold">Rhône Alpes Amitié Voyages</p>
            <h1 className="text-xl sm:text-2xl font-bold mt-1">🔁 Vos participants</h1>
            <p className="text-sm mt-2 text-gray-200">Voyage : <strong>{data.voyage}</strong> ({data.dates}) — aucune pièce d'identité à nous fournir pour ce voyage : le nom, le prénom et la date de naissance de chaque participant suffisent.</p>
          </div>
          <div className="bg-white rounded-b-2xl shadow-lg p-6 space-y-4" data-testid="pieces-simple">
            {data.voyageurs.map(v => (
              <div key={v.index} className="border-2 border-[#79c9ea]/30 rounded-xl p-4">
                <h2 className="font-bold text-[#1E3A5F]">👤 {v.prenom} {v.nom}{v.dateNaissance ? <span className="text-sm font-normal text-gray-500"> · né(e) le {v.dateNaissance}</span> : null}</h2>
                {(data.remplacements_autorises || []).filter(a => a.index === v.index).map(a => (
                  <RemplacerParticipant key={a.id} token={token} autorisation={a} voyageur={v} fields={[]} simple onDone={load} />
                ))}
              </div>
            ))}
            {!(data.remplacements_autorises || []).length && <p className="text-sm text-gray-600" data-testid="pieces-simple-aucun">Aucune modification en attente. Pour remplacer un participant, écrivez-nous d'abord : nous débloquons ensuite le remplacement ici.</p>}
            <p className="text-xs text-gray-400 text-center">Une question ? WhatsApp : 06 60 41 62 18</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAF7F2] py-8 px-4">
      <div className="max-w-2xl mx-auto">
        <div className="bg-[#1E3A5F] text-white rounded-t-2xl p-6">
          <p className="text-sm text-[#79c9ea] font-semibold">Rhône Alpes Amitié Voyages</p>
          {isAvion && <img src="/monbillet-logo.svg" alt="Mon billet d'avion" className="h-7 w-auto mt-2 bg-white rounded-md px-2 py-1" data-testid="monbillet-logo-page" />}
          <h1 className="text-xl sm:text-2xl font-bold mt-1">{isAvion ? '✈️ Mon billet d\'avion — vos informations voyageur' : '🛂 Vos informations voyageur'}</h1>
          <p className="text-sm mt-2 text-gray-200">
            Voyage : <strong>{data.voyage}</strong> ({data.dates}) — {isAvion
              ? <>la compagnie aérienne nous demande ces informations pour chaque voyageur. Merci de les renseigner depuis votre
                <strong> passeport en cours de validité</strong>{passeportOnly ? '' : ' (ou votre carte d\'identité si elle est acceptée pour cette destination — voir le descriptif du voyage)'}.</>
              : <>le transporteur et les hôtels nous demandent ces informations pour chaque voyageur.
                Pour ce voyage en autocar, <strong>la carte d'identité en cours de validité est suffisante</strong> —
                le passeport est bien sûr accepté aussi.</>}
          </p>
          {passeportOnly && (
            <p className="text-xs mt-2 bg-red-500/20 border border-red-300/40 rounded-lg px-3 py-2 text-red-100 font-semibold" data-testid="pieces-passeport-only-note">
              🛂 Pour ce voyage, seul le <strong>PASSEPORT</strong> est accepté — la carte d'identité est refusée à l'embarquement.
            </p>
          )}
        </div>
        <div className="bg-white rounded-b-2xl shadow-lg p-6 space-y-6" data-testid="pieces-form">
          <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-4" data-testid="pieces-passeport-attente-note">
            <p className="text-sm text-blue-900">
              {isAvion ? (
                <>
                  💡 <strong>Vous n'avez pas encore votre passeport</strong> (expiré, en cours de renouvellement ou pas encore demandé) ?
                  <strong> Pas de panique</strong> : ce lien <strong>n'expire pas</strong>. Revenez remplir ce formulaire dès que votre
                  passeport sera prêt — et si vous égarez le message, il suffit de nous redemander le lien. 😊<br />
                  <strong>En revanche, ne tardez pas à lancer les démarches</strong> : les délais de rendez-vous en mairie
                  peuvent être longs, et le passeport doit être prêt bien avant le départ.
                  {data.date_depart && (() => {
                    // Iter 579 (Kamel) — au plus tard 60 jours avant le départ, indispensable pour la convocation
                    const d = new Date(data.date_depart + 'T00:00:00'); d.setDate(d.getDate() - 60);
                    const depassee = d < new Date(new Date().toDateString());
                    return <span className="block mt-2 font-bold text-red-800" data-testid="pieces-limite-j60">🔒 Ces informations sont indispensables pour recevoir votre convocation de départ : {depassee ? 'le délai des 60 jours avant le départ est dépassé, merci de les transmettre aujourd\'hui.' : `à transmettre au plus tard le ${d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })} (60 jours avant le départ).`}</span>;
                  })()}
                </>
              ) : (
                <>
                  💡 <strong>Votre pièce d'identité est expirée ou en cours de renouvellement ?</strong>
                  <strong> Pas de panique</strong> : ce lien <strong>n'expire pas</strong>. Revenez remplir ce formulaire dès que votre
                  nouvelle pièce sera prête — et si vous égarez le message, il suffit de nous redemander le lien. 😊
                </>
              )}
            </p>
          </div>
          <LiterieChoix token={token} literie={data.literie} verrouille={data.verrouille} onChange={(l) => setData(d => ({ ...d, literie: l }))} />
          {(() => {
            const keys = data.fields?.length ? data.fields : ALL_FIELDS.map(f => f.key);
            const fields = fieldDefs(data).filter(f => keys.includes(f.key));
            return data.voyageurs.map(v => (
            <div key={v.index} className="border-2 border-[#79c9ea]/30 rounded-xl p-4">
              <h2 className="font-bold text-[#1E3A5F] mb-3">👤 {v.prenom} {v.nom}</h2>
              {(data.remplacements_autorises || []).filter(a => a.index === v.index).map(a => (
                <RemplacerParticipant key={a.id} token={token} autorisation={a} voyageur={v} fields={fields} onDone={load} />
              ))}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {fields.map(f => (
                  <div key={f.key}>
                    <label className="text-xs font-bold text-gray-600">{f.label}</label>
                    {f.type === 'nationalite' ? (
                      <>
                        <select
                          value={autreNat[v.index] || (form[v.index]?.nationalite && !NATIONALITES.includes(form[v.index].nationalite)) ? 'Autre' : (form[v.index]?.nationalite || '')}
                          onChange={e => {
                            if (e.target.value === 'Autre') {
                              setAutreNat(p => ({ ...p, [v.index]: true }));
                              setVal(v.index, 'nationalite', '');
                            } else {
                              setAutreNat(p => ({ ...p, [v.index]: false }));
                              setVal(v.index, 'nationalite', e.target.value);
                            }
                          }}
                          data-testid={`pieces-${v.index}-nationalite`}
                          className="w-full border rounded-lg px-3 py-2 text-sm mt-1">
                          {NATIONALITES.map(o => <option key={o} value={o}>{o}</option>)}
                          <option value="Autre">Autre…</option>
                        </select>
                        {(autreNat[v.index] || (form[v.index]?.nationalite && !NATIONALITES.includes(form[v.index].nationalite))) && (
                          <input
                            type="text"
                            value={form[v.index]?.nationalite || ''}
                            onChange={e => setVal(v.index, 'nationalite', e.target.value)}
                            placeholder="Précisez la nationalité"
                            data-testid={`pieces-${v.index}-nationalite-autre`}
                            className="w-full border rounded-lg px-3 py-2 text-sm mt-1"
                          />
                        )}
                      </>
                    ) : f.type === 'select' ? (
                      <select
                        value={form[v.index]?.[f.key] || ''}
                        onChange={e => setVal(v.index, f.key, e.target.value)}
                        data-testid={`pieces-${v.index}-${f.key}`}
                        className={`w-full border rounded-lg px-3 py-2 text-sm mt-1${missCls(v.index, f.key)}`}>
                        <option value="">— Choisir —</option>
                        {(f.key === 'typeIdentite' && passeportOnly ? ['Passeport'] : f.options).map(o => <option key={o} value={o}>{o}</option>)}
                      </select>
                    ) : (
                      <input
                        type="text"
                        inputMode={f.key.startsWith('date') ? 'numeric' : undefined}
                        value={form[v.index]?.[f.key] || ''}
                        onChange={e => setVal(v.index, f.key, f.key.startsWith('date') ? formatDateInput(e.target.value) : e.target.value)}
                        placeholder={f.placeholder}
                        data-testid={`pieces-${v.index}-${f.key}`}
                        className={`w-full border rounded-lg px-3 py-2 text-sm mt-1${missCls(v.index, f.key)}`}
                      />
                    )}
                    {f.key === 'dateExpiration' && expWarning(form[v.index]?.dateExpiration || '', form[v.index]?.nationalite) && (
                      <p className="text-xs font-semibold text-red-700 bg-red-50 border border-red-200 rounded-lg px-2 py-1.5 mt-1"
                        data-testid={`pieces-${v.index}-expiration-warning`}>
                        {expWarning(form[v.index]?.dateExpiration || '', form[v.index]?.nationalite)}
                      </p>
                    )}
                    {f.key === 'dateExpiration' && !!data?.nationalite_exemptee_6mois
                      && (form[v.index]?.nationalite || '').trim().toLowerCase() === data.nationalite_exemptee_6mois.toLowerCase()
                      && !expWarning(form[v.index]?.dateExpiration || '', form[v.index]?.nationalite) && (
                      <p className="text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg px-2 py-1.5 mt-1"
                        data-testid={`pieces-${v.index}-exemption-note`}>
                        ✅ Passeport {data.nationalite_exemptee_6mois.toLowerCase()[0] === 'a' ? 'algérien' : data.nationalite_exemptee_6mois} pour ce voyage : la règle des 6 mois ne s'applique pas — il doit simplement être valide jusqu'au retour.
                      </p>
                    )}
                  </div>
                ))}
              </div>
            </div>
            ));
          })()}
          <div className="bg-amber-50 border-2 border-amber-200 rounded-xl p-4">
            <p className="text-sm text-amber-900 font-semibold mb-2">
              ⚠️ Recopiez les informations EXACTEMENT comme sur votre pièce d'identité — la compagnie aérienne et la police aux frontières refusent l'embarquement à la moindre différence.
            </p>
            {data?.controle?.actif && (
                <p className="mb-2 text-sm text-amber-900 bg-amber-50 border border-amber-300 rounded-lg p-2.5" data-testid="pieces-controle-banner">
                  ⏳ <strong>Le listing de la compagnie aérienne est en cours de finalisation</strong> ({data.controle.motif}). Vous pouvez encore compléter une information manquante ; toute <strong>modification d'une information déjà saisie</strong> sera d'abord vérifiée par notre équipe avant d'être prise en compte — nous vous confirmons rapidement.
                  {data.modifs_en_attente > 0 && <span className="block mt-1">{data.modifs_en_attente} modification(s) déjà en attente de validation.</span>}
                </p>
              )}
            <label className="flex items-start gap-2 cursor-pointer">
              <input type="checkbox" checked={certifie} onChange={e => setCertifie(e.target.checked)}
                data-testid="pieces-certifie-checkbox" className="mt-0.5 w-4 h-4" />
              <span className="text-sm text-amber-900">
                Je certifie avoir recopié ces informations <strong>exactement</strong> depuis la pièce d'identité de chaque voyageur (et non de mémoire).
              </span>
            </label>
          </div>
          {!isComplete && (
            <div className="bg-red-50 border-2 border-red-200 rounded-xl p-4" data-testid="pieces-missing-banner">
              <p className="text-sm font-bold text-red-700 mb-1">
                🔒 Formulaire incomplet — la compagnie aérienne exige TOUTES les informations, sans exception.
              </p>
              {missingByVoyageur.map((m, i) => (
                <p key={i} className="text-xs text-red-700">• <strong>{m.nom}</strong> : {m.manque.join(', ')}</p>
              ))}
            </div>
          )}
          {data.verrouille ? (
            <div className="bg-red-50 border-2 border-red-300 rounded-xl p-4 text-sm text-red-900" data-testid="pieces-verrou-banner">
              🔒 <strong>La liste des voyageurs a été transmise à la compagnie</strong> : les informations ne peuvent plus être modifiées en ligne. Une correction indispensable ? Appelez-nous vite au <strong>06 60 41 62 18</strong>, nous la ferons pour vous.
            </div>
          ) : (
          <button
            onClick={submit}
            disabled={sending || !certifie || !isComplete}
            data-testid="pieces-submit-btn"
            className="w-full bg-[#1E3A5F] text-white font-bold py-3 rounded-xl hover:bg-[#2a4d7a] disabled:opacity-50">
            {sending ? 'Enregistrement…' : isComplete ? '✅ Enregistrer mes informations' : '🔒 Complétez toutes les cases pour valider'}
          </button>
          )}
          <p className="text-xs text-gray-400 text-center">
            Vos données restent confidentielles et servent uniquement à l'organisation de votre voyage.
            Une question ? WhatsApp : 06 60 41 62 18
          </p>
        </div>
      </div>
    </div>
  );
}
